import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Card, CardBody, Col, Container, Row,
  Form, FormGroup, Label, Input, Button, CardHeader
} from 'reactstrap';
import MetaTags from "react-meta-tags";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Link, useHistory } from "react-router-dom";
import Cropper from 'react-easy-crop';
import PlanSelectionModal from "../Components/PlanSelectionModal";
import { getDealCroppedImg } from '../../Components/Common/DealCrop';
import { getSubscription } from "../BaseUrl/SubscriptionApi";
import TimePicker from "rc-time-picker";
import moment from "moment";
import "rc-time-picker/assets/index.css";

function AddHH() {
  const history = useHistory();

  const [formData, setFormData] = useState({
    name: '',
    categoryId: '',
    days: [],
    startTime: '',
    endTime: '',
    dealPhoto: [],
    removePhotoIds: [],
    businessId: localStorage.getItem("businessId") || ''
  });

  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [dealPhotoPreviews, setDealPhotoPreviews] = useState([]);
  const [selectedFileNames, setSelectedFileNames] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const fileInputRef = useRef(null);
  const [showUpgradeBanner, setShowUpgradeBanner] = useState(false);

  // Cropper states
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState(null);
  const [currentCropIndex, setCurrentCropIndex] = useState(null);

  const [subscription, setSubscription] = useState(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(true);

  const daysOptions = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  const isFreePlan = subscriptionLoading ? false : (!subscription || subscription.subscription === null || subscription.subscription?.planName === "free");

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BASE_URL}categories`);
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setCategories(data.responseData?.categories || []);
      } catch (error) {
        console.error('Error fetching categories:', error);
        toast.error(`Failed to load categories: ${error.message}`);
      } finally {
        setCategoriesLoading(false);
      }
    };

    const fetchSub = async () => {
      try {
        const res = await getSubscription();
        setSubscription(res?.responseData || null);
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
      } finally {
        setSubscriptionLoading(false);
      }
    };

    fetchCategories();
    fetchSub();
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (dealPhotoPreviews.length >= 2) {
        toast.error("You can only upload up to 2 images.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setImageToCrop(reader.result);
        setCurrentCropIndex(dealPhotoPreviews.length);
        setShowCropper(true);
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = async () => {
    try {
      const croppedImageBlob = await getDealCroppedImg(imageToCrop, croppedAreaPixels);
      const fileName = 'deal-photo.jpg';
      const croppedImageFile = new File([croppedImageBlob], fileName, { type: 'image/jpeg' });
      const previewUrl = URL.createObjectURL(croppedImageBlob);
      setFormData(prev => ({ ...prev, dealPhoto: [...prev.dealPhoto, croppedImageFile] }));
      setDealPhotoPreviews(prev => [...prev, previewUrl]);
      setSelectedFileNames(prev => [...prev, fileName]);
      setShowCropper(false);
    } catch (e) {
      toast.error('Error cropping image');
      console.error('Error cropping image', e);
    }
  };

  const handleRemovePhoto = (index, photoId = null) => {
    if (photoId) {
      setFormData(prev => ({
        ...prev,
        removePhotoIds: [...prev.removePhotoIds, photoId]
      }));
    }
    setFormData(prev => ({
      ...prev,
      dealPhoto: prev.dealPhoto.filter((_, i) => i !== index)
    }));
    setDealPhotoPreviews(prev => prev.filter((_, i) => i !== index));
    setSelectedFileNames(prev => prev.filter((_, i) => i !== index));
  };

  const handleDayChange = (day) => {
    setFormData(prev => {
      const days = [...prev.days];
      if (days.includes(day)) {
        return { ...prev, days: days.filter(d => d !== day) };
      } else {
        if (isFreePlan && days.length >= 5) {
          setShowUpgradeBanner(true);
          return prev;
        }
        return { ...prev, days: [...days, day] };
      }
    });
  };

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!formData.startTime || !formData.endTime) {
    toast.error("Please select both start and end times.");
    return;
  }

  const today = moment().format("YYYY-MM-DD");
  let startMoment = moment(`${today}T${formData.startTime}`);
  let endMoment = moment(`${today}T${formData.endTime}`);

  // ✅ Handle overnight deals
  if (!endMoment.isAfter(startMoment)) {
    endMoment.add(1, 'day'); // next-day rollover
  }

  // ✅ Check duration <= 12 hours
  const durationHours = moment.duration(endMoment.diff(startMoment)).asHours();
  if (durationHours > 12) {
    toast.error(`Happy Hours deal cannot exceed 12 hours. Current duration: ${Math.floor(durationHours)}h ${Math.floor((durationHours % 1) * 60)}m`);
    return;
  }

  setLoading(true);
  try {
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('categoryId', formData.categoryId);
    formDataToSend.append('businessId', formData.businessId);

    const startISO = startMoment.format("YYYY-MM-DDTHH:mm:ss");
    const endISO = endMoment.format("YYYY-MM-DDTHH:mm:ss");

    formDataToSend.append('startTime', startISO);
    formDataToSend.append('endTime', endISO);

    formData.dealPhoto.forEach(photo => formDataToSend.append('dealPhoto', photo));
    formData.removePhotoIds.forEach(id => formDataToSend.append('removePhotoIds[]', id));
    formData.days.forEach(day => formDataToSend.append('days[]', day));

    const response = await fetch(`${process.env.REACT_APP_BASE_URL}business/deal/happyHours`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${localStorage.getItem('accessToken')}` },
      body: formDataToSend
    });

    const data = await response.json();

    if (!response.ok) {
      if (data.responseMessage && Array.isArray(data.responseMessage)) {
        throw new Error(data.responseMessage.join(', '));
      } else {
        throw new Error(data.responseMessage || `Request failed with status ${response.status}`);
      }
    }

    const isSuccess = data.responseCode === 200 ||
      (Array.isArray(data.responseMessage) &&
        data.responseMessage.some(msg =>
          msg.toLowerCase().includes('success') ||
          msg.toLowerCase().includes('successfully')
        ));

    if (isSuccess) {
      toast.success('Happy Hours deal added successfully!');
      setFormData({
        name: '',
        categoryId: '',
        days: [],
        startTime: '',
        endTime: '',
        dealPhoto: [],
        removePhotoIds: [],
        businessId: localStorage.getItem("businessId") || ''
      });
      setDealPhotoPreviews([]);
      setSelectedFileNames([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      setTimeout(() => history.push('/happy-hour-deal-list'), 1500);
    } else {
      throw new Error(data.responseMessage?.join(', ') || 'Failed to add Happy Hours deal');
    }
  } catch (error) {
    console.error('Error submitting form:', error);
    toast.error(`${error.message}`);
  } finally {
    setLoading(false);
  }
};



  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleSubscriptionSuccess = () => {
    setShowUpgradeBanner(false);
    setIsModalOpen(false);
    const fetchSub = async () => {
      try {
        const res = await getSubscription();
        setSubscription(res?.responseData || null);
      } catch (err) {
        console.error("Failed to fetch subscription:", err);
      } finally {
        setSubscriptionLoading(false);
      }
    };
    fetchSub();
  };

  return (
    <div className="page-content">
      <Container fluid className="mt-5">
        <MetaTags><title>Add Happy Hours Deal</title></MetaTags>
       <ToastContainer/>


        <PlanSelectionModal
          isOpen={isModalOpen}
          toggle={toggleModal}
          isUpgrade={true}
          onSubscriptionSuccess={handleSubscriptionSuccess}
        />

        <Row>
          <Col lg="12">
            {!subscriptionLoading && showUpgradeBanner && isFreePlan && (
              <div className="alert alert-warning d-flex justify-content-between align-items-center mt-3">
                <span>
                  You've reached the <b>Free Plan</b> limit (5 days).  
                  Upgrade to unlock unlimited selections!
                </span>
                <Button
                  color="primary"
                  size="sm"
                  onClick={() => {
                    setIsModalOpen(true);
                    localStorage.setItem("redirectAfterSubscribe", window.location.pathname);
                  }}
                >
                  {subscription === null ? "Add Card" : "Upgrade Plan"}
                </Button>
              </div>
            )}

            <Card>
              <CardHeader className='d-flex align-items-center'>
                <Link to="/happy-hour-deal-list">
                  <Button color="primary" className="btn">
                    <i className="mdi mdi-arrow-left"></i>
                  </Button>
                </Link>
                <h4 className="card-title mb-0 ms-2">Add Happy Hours Deal</h4>
              </CardHeader>
              <CardBody>
                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Category</Label>
                        <Input
                          type="select"
                          name="categoryId"
                          value={formData.categoryId}
                          onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                          disabled={categoriesLoading}
                        >
                          <option value="">Select a category</option>
                          {categories.map(category => (
                            <option key={category._id} value={category._id}>
                              {category.name}
                            </option>
                          ))}
                        </Input>
                        {categoriesLoading && <small className="text-muted">Loading categories...</small>}
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label>Deal Photos</Label>
                        <div className="d-flex">
                          <Button
                            type="button"
                            color="primary"
                            onClick={() => fileInputRef.current.click()}
                            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                            disabled={dealPhotoPreviews.length >= 2}
                          >
                            Choose File
                          </Button>
                          <Input
                            type="text"
                            readOnly
                            value={selectedFileNames.length > 0
                              ? `${selectedFileNames.length} file(s) selected`
                              : 'No files chosen'}
                            style={{ flex: 1, cursor: "pointer", borderTopLeftRadius: 0, borderBottomLeftRadius: 0 }}
                            onClick={() => fileInputRef.current.click()}
                          />
                          <input
                            type="file"
                            name="dealPhoto"
                            onChange={handleFileChange}
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: "none" }}
                          />
                        </div>
                        {dealPhotoPreviews.length > 0 && (
                          <div className="mt-3">
                            <Label>Preview:</Label>
                            <div className="d-flex flex-wrap gap-2">
                              {dealPhotoPreviews.map((preview, index) => (
                                <div key={index} className="position-relative" style={{ width: '100px', height: '100px' }}>
                                  <img
                                    src={preview}
                                    alt={`Deal preview ${index + 1}`}
                                    style={{
                                      width: '100%',
                                      height: '100%',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #ddd'
                                    }}
                                  />
                                  <Button
                                    color="danger"
                                    size="sm"
                                    className="position-absolute"
                                    style={{
                                      top: '-10px',
                                      right: '-10px',
                                      width: '20px',
                                      height: '20px',
                                      borderRadius: '50%',
                                      display: 'flex',
                                      alignItems: 'center',
                                      justifyContent: 'center',
                                      padding: 0
                                    }}
                                    onClick={() => handleRemovePhoto(index)}
                                  >
                                    ×
                                  </Button>
                                </div>
                              ))}
                            </div>
                            {dealPhotoPreviews.length >= 2 && (
                              <small className="text-muted">Maximum 2 images allowed</small>
                            )}
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* ✅ Updated Time Range Picker */}
                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Time Range</Label>
                        <div className="d-flex align-items-center gap-2">
                          <TimePicker
                            showSecond={false}
                            use12Hours
                            format="h:mm A"
                            value={formData.startTime ? moment(formData.startTime, "HH:mm") : null}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                startTime: value ? value.format("HH:mm") : "",
                              }))
                            }
                            placeholder="Start Time"
                            inputReadOnly
                            style={{ width: "49%" }}
                          />
                          <span>to</span>
                          <TimePicker
                            showSecond={false}
                            use12Hours
                            format="h:mm A"
                            value={formData.endTime ? moment(formData.endTime, "HH:mm") : null}
                            onChange={(value) =>
                              setFormData((prev) => ({
                                ...prev,
                                endTime: value ? value.format("HH:mm") : "",
                              }))
                            }
                            placeholder="End Time"
                            inputReadOnly
                            style={{ width: "49%" }}
                          />
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={12}>
                      <FormGroup>
                        <Label>Days</Label>
                        <div className="d-flex flex-wrap gap-2">
                          {daysOptions.map(day => (
                            <div key={day} className="form-check">
                              <Input
                                type="checkbox"
                                id={`day-${day}`}
                                checked={formData.days.includes(day)}
                                onChange={() => handleDayChange(day)}
                              />
                              <Label for={`day-${day}`} className="form-check-label">
                                {day}
                              </Label>
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <Button type="submit" color="primary" disabled={loading || categoriesLoading}>
                      {loading ? 'Submitting...' : 'Submit'}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>

        {showCropper && (
          <div className="crop-modal">
            <div className="crop-modal-content">
              <h4>Crop your deal photo</h4>
              <div className="crop-container">
                <Cropper
                  image={imageToCrop}
                  crop={crop}
                  zoom={zoom}
                  aspect={2.5 / 3}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                  cropShape="rect"
                  zoomWithWheel={true}
                  minZoom={0.5}
                  maxZoom={3}
                  wheelSpeed={0.01}
                />
              </div>
              <div className="crop-controls">
                <input
                  type="range"
                  value={zoom}
                  min={0.5}
                  max={3}
                  step={0.01}
                  onChange={(e) => setZoom(e.target.value)}
                />
                <div className="crop-buttons">
                  <Button color="secondary" onClick={() => setShowCropper(false)}>
                    Cancel
                  </Button>
                  <Button color="primary" onClick={handleCropComplete}>
                    Save Crop
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </Container>
    </div>
  );
}

export default AddHH;
